"use client";

import { ShieldCheck, Sparkles, Zap } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { SpotToast } from "@/components/SpotToast";
import { GenieMode, UsersService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS } from "@/utils/useRemoteData";

const MODES = [
  {
    value: GenieMode.DEFAULT,
    label: "Ask",
    hint: "Asks before every change",
    Icon: ShieldCheck,
  },
  {
    value: GenieMode.AUTO,
    label: "Auto",
    hint: "Asks only before deleting",
    Icon: Sparkles,
  },
  {
    value: GenieMode.YOLO,
    label: "Yolo",
    hint: "Stops deletes you did not ask for",
    Icon: Zap,
  },
] as const;

function isGenieMode(value: string): value is GenieMode {
  return MODES.some((mode) => mode.value === value);
}

const BUTTON_VARIANT = {
  [GenieMode.DEFAULT]: "outline",
  [GenieMode.AUTO]: "outlineWarning",
  [GenieMode.YOLO]: "outlineDestructive",
} as const;

export function GenieModeMenu({ disabled = false }: { disabled?: boolean }) {
  const { spotUser } = useSpotUser();
  const queryClient = useQueryClient();
  const current =
    MODES.find((mode) => mode.value === spotUser.genie_mode) ?? MODES[0];

  const updateMode = useMutation({
    mutationFn: UsersService.updateMe,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.USER] }),
    onError: () => SpotToast.error("Could not change the Genie mode"),
  });

  return (
    <DropdownMenu>
      <NoxusTooltip content={current.hint} placement="top" asChild>
        <DropdownMenuTrigger asChild>
          <Button
            variant={BUTTON_VARIANT[current.value]}
            size="xs"
            className="px-1.5"
            aria-label={`Genie mode: ${current.label}`}
            disabled={disabled || updateMode.isPending}
          >
            <current.Icon />
            {current.label}
          </Button>
        </DropdownMenuTrigger>
      </NoxusTooltip>
      <DropdownMenuContent side="top" align="start" className="w-64">
        <DropdownMenuLabel>Approval mode</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={current.value}
          onValueChange={(value) => {
            if (isGenieMode(value) && value !== current.value) {
              updateMode.mutate({ body: { genie_mode: value } });
            }
          }}
        >
          {MODES.map(({ value, label, hint, Icon }) => (
            <DropdownMenuRadioItem key={value} value={value} className="gap-2">
              <Icon className="h-4 w-4 shrink-0" />
              <div className="flex flex-col">
                <span className="text-sm">{label}</span>
                <span className="text-xs text-muted-foreground">{hint}</span>
              </div>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
